<template>
  <el-image
    ref="imageRef"
    :class="imageClass"
    :src="currentSrc"
    :fit="fit"
    @load="handleLoad"
    @error="handleError"
  >
    <template #placeholder>
      <div class="image-placeholder">
        <i class="el-icon-loading"></i>
      </div>
    </template>
    <template #error>
      <div class="image-error">
        <i class="el-icon-picture-outline"></i>
      </div>
    </template>
  </el-image>
</template>

<script>
export default {
  name: 'RetryImage',
  props: {
    src: {
      type: String,
      default: '',
    },
    fit: {
      type: String,
      default: 'contain',
    },
    imageClass: {
      type: String,
      default: '',
    },
    // 最大重试次数
    maxRetry: {
      type: Number,
      default: 3,
    },
    // 重试间隔（毫秒）
    retryDelay: {
      type: Number,
      default: 1500,
    },
    // 最小有效图片宽度（用于检测404 gif）
    minValidWidth: {
      type: Number,
      default: 50,
    },
    // 最小有效图片高度
    minValidHeight: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      currentSrc: '',
      retryCount: 0,
      retryTimer: null,
    };
  },
  watch: {
    src: {
      immediate: true,
      handler(newSrc) {
        this.retryCount = 0;
        this.clearRetryTimer();
        this.currentSrc = newSrc;
      },
    },
  },
  beforeDestroy() {
    this.clearRetryTimer();
  },
  methods: {
    /**
     * 图片加载成功回调
     * 检测图片尺寸，如果太小可能是404 gif，触发重试
     */
    handleLoad(e) {
      const img = e.target || e.srcElement;
      if (!img) return;

      const { naturalWidth, naturalHeight } = img;

      // 检测是否是有效图片（排除404返回的小gif）
      if (naturalWidth < this.minValidWidth || naturalHeight < this.minValidHeight) {
        console.log(`[RetryImage] 检测到可能的404图片，尺寸: ${naturalWidth}x${naturalHeight}`);
        this.scheduleRetry();
        return;
      }

      // 图片加载成功且尺寸正常
      this.$emit('load', e);
    },
    /**
     * 图片加载失败回调
     */
    handleError(e) {
      console.log('[RetryImage] 图片加载失败');
      this.scheduleRetry();
      this.$emit('error', e);
    },
    /**
     * 安排重试
     */
    scheduleRetry() {
      if (this.retryCount >= this.maxRetry) {
        console.log(`[RetryImage] 已达到最大重试次数: ${this.maxRetry}`);
        this.$emit('retry-failed');
        return;
      }

      this.retryCount++;
      console.log(`[RetryImage] 将在 ${this.retryDelay}ms 后进行第 ${this.retryCount} 次重试`);

      this.clearRetryTimer();
      this.retryTimer = setTimeout(() => {
        this.reloadImage();
      }, this.retryDelay);
    },
    /**
     * 重新加载图片
     */
    reloadImage() {
      // 通过添加时间戳参数强制重新请求
      const separator = this.src.includes('?') ? '&' : '?';
      this.currentSrc = `${this.src}${separator}_retry=${Date.now()}`;
    },
    /**
     * 清除重试定时器
     */
    clearRetryTimer() {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.image-placeholder,
.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 24px;
}
</style>
